import { apiRequest, type components } from './core';

export interface TenantAuditLog {
  id: string;
  entityType: string;
  entityId: string;
  action: 'created' | 'updated' | 'deleted' | 'photo_uploaded' | 'photo_deleted';
  userId: string;
  userName: string | null;
  userEmail: string | null;
  oldValue: Record<string, unknown> | null;
  newValue: Record<string, unknown> | null;
  ipAddress: string | null;
  userAgent: string | null;
  createdAt: string;
}

export interface TenantAuditLogsResponse {
  success: boolean;
  logs: TenantAuditLog[];
  total: number;
  limit: number;
  offset: number;
}

export type EnrichedBehaviorAuditLog = components['schemas']['EnrichedBehaviorAuditLog'];
export type BehaviorAuditLogsResponse = components['schemas']['BehaviorAuditLogsResponse'];

export const tenantAuditLogsApi = {
  /**
   * Get recent audit activity (for dashboard widgets)
   */
  getRecentActivity: async (limit: number = 10) => {
    return apiRequest<{ success: boolean; data: TenantAuditLog[] }>(`/api/audit-logs/recent/activity?limit=${limit}`);
  },

  /**
   * Get tenant audit logs with filters
   */
  getAuditLogs: async (filters: {
    startDate?: string;
    endDate?: string;
    userId?: string;
    action?: string;
    entityType?: string;
    limit?: number;
    offset?: number;
  }) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined) params.append(key, String(value));
    });
    return apiRequest<TenantAuditLogsResponse>(`/api/audit-logs?${params.toString()}`);
  },

  /**
   * Get specific audit log by ID
   */
  getAuditLogById: async (id: string) => {
    return apiRequest<{ success: boolean; data: TenantAuditLog }>(`/api/audit-logs/${id}`);
  },

  /**
   * Get enriched behavior audit logs (for Behavior Records tab)
   */
  getBehaviorAuditLogs: async (filters: {
    startDate?: string;
    endDate?: string;
    categoryId?: string;
    severity?: 'minor' | 'major';
    studentId?: string;
    studentSearch?: string;
    staffId?: string;
    action?: string;
    behaviorType?: 'positive' | 'negative';
    limit?: number;
    offset?: number;
  }) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined) params.append(key, String(value));
    });
    return apiRequest<BehaviorAuditLogsResponse>(`/api/audit-logs/behavior?${params.toString()}`);
  },
};
